import ConfirmModal from "@/src/components/action-modals/confirm-modal";
import InfoModal from "@/src/components/action-modals/info-modal";
import ContentRichField from "@/src/components/text-editor/content-rich-field";
import BackNavigationButton from "@/src/components/ui-kit/back-navigation";
import ScButton from "@/src/components/ui-kit/button";
import CategorySelector from "@/src/components/ui-kit/category-selector/category-selector";
import ImageUpload from "@/src/components/ui-kit/image-uploader";
import InputField from "@/src/components/ui-kit/input-field.rhf";
import WebDropZone from "@/src/components/ui-kit/web-drop-zone";
import {
  ACCEPTED_ATTACHMENT_TYPES,
  BYTES_PER_MB,
  IMAGE_EXTS,
  MAX_ATTACHMENT_SIZE_MB,
  MAX_IMAGE_SIZE_MB,
  MIME_TO_EXT,
} from "@/src/constants/files-extensions";
import { PickedFile } from "@/src/models/file.models";
import { ConfirmModalConfig, InfoModalConfig } from "@/src/models/form.model";
import { useTheme } from "@/src/styles/theme-provider";
import { webFileToAsset } from "@/src/utils/attachment.utils";
import * as DocumentPicker from "expo-document-picker";
import { Href } from "expo-router";
import { AttachCircle, CloseCircle, Document } from "iconsax-react-nativejs";
import { useState } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Image,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import Toast from "react-native-toast-message";
import {
  getIconBg,
  getIconColor,
} from "../components/ui-kit/attachment-viewer";
import ScToggle from "../components/ui-kit/toggle";
import { CategoryTag } from "../models/category.model";
import ScRadioOption from "../components/ui-kit/radio-option";
import ScChips from "../components/ui-kit/chips";
import ItemSelectorModal from "../components/action-modals/item-selection/item-selection-modal";
import {
  useGroupPickerItems,
  useSelectedGroupItems,
} from "../services/api/groups/groups.hooks";

const getFileExtension = (url: string, mimeType?: string): string => {
  const filename = url.split("/").pop()?.split("?")[0] ?? "";
  const fromPath = filename.split(".").pop()?.toLowerCase() ?? "";
  if (fromPath && fromPath.length <= 5 && fromPath !== filename.toLowerCase()) {
    return fromPath;
  }
  if (mimeType) return MIME_TO_EXT[mimeType] ?? "file";
  return fromPath || "file";
};

const isImageFile = (url: string, mimeType?: string): boolean =>
  IMAGE_EXTS.includes(getFileExtension(url, mimeType));

export type FormFieldConfig<T extends FieldValues> = Readonly<{
  name: Path<T>;
  label: string;
  placeholder: string;
  keyboardType?: "default" | "email-address" | "numeric" | "phone-pad";
}>;

export type FormAction = Readonly<{
  label: string;
  onPress: () => void;
  mode?: "contained" | "outlined" | "text";
  disabled?: boolean;
  loading?: boolean;
}>;

export type ContentCreationFormProps<T extends FieldValues> = Readonly<{
  title: string;
  backTo?: Href;
  onBack?: () => void;
  contentField?: Path<T>;

  control: Control<T>;
  fields: readonly FormFieldConfig<T>[];

  categoryEntityType: string;
  selectedCategoryIds: string[];
  onSelectedCategoryIdsChange: (ids: string[]) => void;
  maxCategories?: number;

  coverImage?: PickedFile;
  onCoverImageChange?: (file: PickedFile) => void;
  coverImageLabel?: string;

  attachments?: PickedFile[];
  onAddAttachments?: (files: PickedFile[]) => void;
  onRemoveAttachment?: (index: number) => void;
  showAttachments?: boolean;

  primaryActions: readonly FormAction[];
  secondaryAction?: FormAction;
  /**
   * Extra actions grouped with `secondaryAction` on the left (e.g. "Save as
   * Draft" sitting next to "Cancel"), instead of the right-aligned
   * `primaryActions` cluster. Rendered in order, after `secondaryAction`.
   */
  secondaryActions?: readonly FormAction[];

  isLoading?: boolean;

  infoModal?: InfoModalConfig;
  confirmModal?: ConfirmModalConfig;
  acceptedAttachmentTypes?: string[];
  attachmentHint?: string;

  attachmentAcceptedMimeTypes?: string[];
  attachmentError?: string | null;
  onAttachmentDropRejected?: () => void;

  showPinToggle?: boolean;
  isPinned?: boolean;
  onPinChange?: (value: boolean) => void;

  showVisibility?: boolean;
  visibility?: "Public" | "Group";
  onVisibilityChange?: (visibility: "Public" | "Group") => void;
  groupIds?: string[];
  onGroupIdsChange?: (ids: string[]) => void;
  visibilityError?: string;
}>;

export default function ContentCreationForm<T extends FieldValues>({
  title,
  backTo,
  onBack,
  control,
  fields,
  categoryEntityType,
  selectedCategoryIds,
  onSelectedCategoryIdsChange,
  maxCategories,
  coverImage,
  onCoverImageChange,
  coverImageLabel = "Cover Image",
  attachments = [],
  onAddAttachments,
  onRemoveAttachment,
  showAttachments = true,
  showPinToggle = false,
  isPinned = false,
  onPinChange,
  primaryActions,
  secondaryAction,
  secondaryActions = [],
  isLoading = false,
  infoModal,
  confirmModal,
  contentField,
  acceptedAttachmentTypes,
  attachmentHint,
  attachmentAcceptedMimeTypes,
  attachmentError,
  onAttachmentDropRejected,
  showVisibility = false,
  visibility = "Public",
  onVisibilityChange,
  groupIds = [],
  onGroupIdsChange,
  visibilityError,
}: ContentCreationFormProps<T>) {
  const { t } = useTranslation();
  const { primaryPalette, themeVars } = useTheme();

  const resolveColor = (cssVar: string): string =>
    (themeVars[cssVar] as string) ?? "#888";

  const [coverImageError, setCoverImageError] = useState<string | null>(null);

  const [groupModalVisible, setGroupModalVisible] = useState(false);

  // Resolved by id so a group past the picker's loaded pages still shows.
  const selectedGroups = useSelectedGroupItems(groupIds);
  const groupPicker = useGroupPickerItems({}, groupModalVisible);

  const handleGroupConfirm = (ids: string[]) => {
    onGroupIdsChange?.(ids);
  };

  const handleRemoveGroup = (id: string) => {
    onGroupIdsChange?.(groupIds.filter((groupId) => groupId !== id));
  };

  const filterBySize = <F extends { name?: string; size?: number }>(
    files: F[],
  ): F[] => {
    const valid: F[] = [];
    files.forEach((file) => {
      if (file.size && file.size > MAX_ATTACHMENT_SIZE_MB * BYTES_PER_MB) {
        Toast.show({
          type: "error",
          text1: t("ERROR_ATTACHMENT_TOO_LARGE", {
            fileName: file.name ?? t("LABEL_ATTACHMENT"),
            maxSize: MAX_ATTACHMENT_SIZE_MB,
          }),
        });
        return;
      }
      valid.push(file);
    });
    return valid;
  };

  const pickAttachments = async () => {
    const result = await DocumentPicker.getDocumentAsync({
      type: acceptedAttachmentTypes ?? [...ACCEPTED_ATTACHMENT_TYPES],
      copyToCacheDirectory: true,
      multiple: true,
    });
    if (!result.canceled && result.assets?.length) {
      const valid = filterBySize(result.assets);
      if (valid.length > 0) {
        onAddAttachments?.(valid as unknown as PickedFile[]);
      }
    }
  };

  const handleDropAttachments = (files: File[]) => {
    const assets = files.map(webFileToAsset);
    const valid = filterBySize(assets);
    if (valid.length > 0) {
      onAddAttachments?.(valid as unknown as PickedFile[]);
    }
  };

  return (
    <>
      {infoModal && (
        <InfoModal
          visible={infoModal.visible}
          title={infoModal.title}
          description={infoModal.description}
          variant={infoModal.variant}
          onConfirm={infoModal.onConfirm}
          confirmText={infoModal.confirmText}
        />
      )}
      {confirmModal && (
        <ConfirmModal
          visible={confirmModal.visible}
          title={confirmModal.title}
          description={confirmModal.description}
          confirmText={confirmModal.confirmText}
          cancelText={confirmModal.cancelText}
          onConfirm={confirmModal.onConfirm}
          onCancel={confirmModal.onCancel}
        />
      )}

      <ScrollView
        className="flex-1 bg-obsidian"
        contentContainerStyle={{ alignItems: "center", paddingVertical: 16 }}
        showsVerticalScrollIndicator={false}
      >
        <View className="bg-chalk w-full md:w-[90%] rounded-2xl p-4 md:border md:border-ash-3">
          <View className="flex-row items-center justify-center rounded-2xl p-3 md:p-4 relative mb-4 md:border md:border-ash-3">
            <View className="absolute left-4 top-5 justify-center">
              <BackNavigationButton
                to={backTo}
                onPress={onBack}
                labelClassName="text-onyx"
                iconClassName="text-onyx"
              />
            </View>
            <Text className="text-lg font-semibold text-onyx">{title}</Text>
          </View>

          {fields.map((field) => {
            if (field.name === contentField) {
              return (
                <ContentRichField
                  key={String(field.name)}
                  label={field.label}
                  name={field.name}
                  control={control}
                />
              );
            }

            return (
              <View key={String(field.name)}>
                <Text className="font-medium mb-2 text-onyx">
                  {field.label}
                </Text>
                <InputField
                  showLabel={false}
                  name={field.name}
                  control={control}
                  keyboardType={field.keyboardType ?? "default"}
                  backgroundColor="bg-obsidian"
                  placeholder={field.placeholder}
                  height={12}
                  returnKeyType="next"
                  blurOnSubmit
                />
              </View>
            );
          })}

          <CategorySelector
            label={t("LABEL_CATEGORY")}
            entityType={categoryEntityType as CategoryTag}
            selectedIds={selectedCategoryIds}
            onSelectedIdsChange={onSelectedCategoryIdsChange}
            maxCategories={maxCategories}
          />

          {showVisibility && (
            <View className="mb-4">
              <Text className="font-medium mb-2 text-onyx">
                {t("LABEL_VISIBILITY_OPTIONS")}
              </Text>
              <View className="rounded-xl overflow-hidden md:border md:border-ash-3">
                {[
                  {
                    value: "Public" as const,
                    label: "LABEL_PUBLIC",
                    description: "HINT_PUBLIC_VISIBILITY",
                  },
                  {
                    value: "Group" as const,
                    label: "LABEL_GROUP",
                    description: "HINT_GROUP_VISIBILITY",
                  },
                ].map((option, index) => (
                  <View key={option.value}>
                    {index > 0 && <View className="h-px bg-ash-3" />}
                    <ScRadioOption
                      selected={visibility === option.value}
                      onSelect={() => {
                        onVisibilityChange?.(option.value);
                        if (option.value === "Group") {
                          setGroupModalVisible(true);
                        }
                      }}
                      label={t(option.label)}
                      description={t(option.description)}
                    />
                    {option.value === "Group" && visibility === "Group" && (
                      <View className="px-4 pb-3 pl-12 gap-2 mt-2">
                        {selectedGroups.length > 0 ? (
                          <ScChips
                            items={selectedGroups}
                            onRemove={handleRemoveGroup}
                          />
                        ) : (
                          <ScButton
                            text={t("BTN_SELECT_GROUPS")}
                            variant="pill"
                            className="self-start"
                            onPress={() => setGroupModalVisible(true)}
                          />
                        )}
                        {selectedGroups.length > 0 && (
                          <ScButton
                            text={t("BTN_EDIT_SELECTED_GROUPS")}
                            variant="pill"
                            className="self-start"
                            onPress={() => setGroupModalVisible(true)}
                          />
                        )}
                        {visibilityError && (
                          <Text className="text-crimson text-xs">
                            {visibilityError}
                          </Text>
                        )}
                      </View>
                    )}
                  </View>
                ))}
              </View>
              <ItemSelectorModal
                items={groupPicker.items}
                selectedItems={selectedGroups}
                visible={groupModalVisible}
                onClose={() => setGroupModalVisible(false)}
                onConfirm={handleGroupConfirm}
                titleKey="LABEL_SELECT_GROUPS"
                confirmButtonKey="BTN_ADD"
                searchPlaceholderKey="LABEL_SEARCH"
                onSearchChange={groupPicker.onSearchChange}
                onEndReached={groupPicker.onEndReached}
                isFetchingMore={groupPicker.isFetchingMore}
                totalCount={groupPicker.totalCount}
              />
            </View>
          )}

          {onCoverImageChange && (
            <View className="mb-4">
              <Text className="font-medium mb-2 text-onyx">
                {coverImageLabel}
              </Text>
              <ImageUpload
                label=""
                onFileSelect={(file) => {
                  setCoverImageError(null);
                  onCoverImageChange(file as PickedFile);
                }}
                onDropRejected={() =>
                  setCoverImageError(t("ERROR_INVALID_IMAGE_TYPE"))
                }
                acceptedFormats={t("HINT_IMAGE_ACCEPTED_FORMATS")}
                maxSize={MAX_IMAGE_SIZE_MB}
                previewFile={
                  coverImage && "uri" in coverImage
                    ? {
                      uri: coverImage.uri,
                      fileName: coverImage.fileName ?? undefined,
                    }
                    : null
                }
              />
              {coverImageError && (
                <Text className="text-crimson text-xs mt-1">
                  {coverImageError}
                </Text>
              )}
            </View>
          )}

          {showAttachments && (
            <View className="mb-4">
              <Text className="pb-2 font-medium text-onyx">
                {t("LABEL_ATTACHMENT")}
              </Text>

              <WebDropZone
                onDropFiles={handleDropAttachments}
                acceptedMimeTypes={attachmentAcceptedMimeTypes}
                onDropRejected={onAttachmentDropRejected}
                className="border-2 border-dashed border-ash-3 rounded-lg bg-chalk"
              >
                {attachments.length > 0 ? (
                  <View className="p-4">
                    <View className="flex-row flex-wrap gap-4 mb-4">
                      {attachments.map((attachment, index) => {
                        if (!attachment || !("uri" in attachment)) return null;

                        const anyAtt = attachment as any;
                        let fileName = `Attachment ${index + 1}`;
                        if (anyAtt.fileName) fileName = String(anyAtt.fileName);
                        else if (anyAtt.name) fileName = String(anyAtt.name);
                        else if (attachment.uri) {
                          const last = attachment.uri
                            .split("/")
                            .pop()
                            ?.split("?")[0];
                          if (last?.includes("."))
                            fileName = decodeURIComponent(last);
                        }

                        const mimeType = anyAtt.mimeType as string | undefined;
                        const ext = getFileExtension(attachment.uri, mimeType);
                        const isImage = isImageFile(attachment.uri, mimeType);

                        const iconColorVar = getIconColor(ext);
                        const iconBgVar = getIconBg(ext);
                        const resolvedIconColor = resolveColor(iconColorVar);
                        const resolvedIconBg = resolveColor(iconBgVar);

                        return (
                          <View
                            key={index}
                            className="items-center relative"
                            style={{ width: 100 }}
                          >
                            <View
                              className="w-24 h-24 border border-ash-3 rounded-lg items-center justify-center mb-1 overflow-hidden"
                              style={{
                                backgroundColor: isImage
                                  ? ((themeVars[
                                    "--color-ash-default"
                                  ] as string) ?? "#ccc")
                                  : resolvedIconBg,
                              }}
                            >
                              {isImage ? (
                                <Image
                                  source={{ uri: attachment.uri }}
                                  style={{ width: "100%", height: "100%" }}
                                  resizeMode="cover"
                                />
                              ) : (
                                <Document size={48} color={resolvedIconColor} />
                              )}
                            </View>

                            <TouchableOpacity
                              onPress={() => onRemoveAttachment?.(index)}
                              className="absolute -top-2 -right-2 bg-crimson rounded-full hover:opacity-90"
                            >
                              <CloseCircle size={24} color="white" />
                            </TouchableOpacity>

                            <Text
                              className="text-primary text-xs text-center"
                              numberOfLines={2}
                              style={{ width: 100 }}
                            >
                              {fileName}
                            </Text>
                          </View>
                        );
                      })}
                    </View>

                    <TouchableOpacity
                      onPress={pickAttachments}
                      className="items-center py-6 border-2 border-dashed border-ash-3 rounded-lg hover:bg-ash-3"
                    >
                      <View className="w-12 h-12 bg-ash rounded-full items-center justify-center mb-2">
                        <AttachCircle
                          size={28}
                          color={primaryPalette.default}
                        />
                      </View>
                      <Text className="text-primary font-medium text-sm">
                        {t("BTN_ADD_MORE_FILES")}
                      </Text>
                    </TouchableOpacity>
                  </View>
                ) : (
                  <TouchableOpacity
                    onPress={pickAttachments}
                    className="p-8 items-center justify-center hover:bg-ash-3 hover:rounded-lg"
                  >
                    <View className="w-12 h-12 bg-ash rounded-full items-center justify-center mb-2">
                      <AttachCircle size={28} color={primaryPalette.default} />
                    </View>
                    <Text className="text-onyx font-medium mb-1">
                      {t("LABEL_UPLOAD_OR_DRAG_DROP")}
                    </Text>
                    <Text className="text-ash-1 text-xs">
                      {attachmentHint ?? t("HINT_FILE_UPLOAD_TYPES")}
                    </Text>
                  </TouchableOpacity>
                )}
              </WebDropZone>

              {attachmentError && (
                <Text className="text-crimson text-xs mt-1">
                  {attachmentError}
                </Text>
              )}
            </View>
          )}

          {showPinToggle && (
            <View className="mt-1 mb-4">
              <Text className="font-medium mb-2 text-onyx">
                {t("LABEL_PIN_NEWS")}
              </Text>
              <View className="flex-row items-center justify-between rounded-xl px-4 py-3 md:border md:border-ash-3">
                <Text className="text-sm text-ash-2 flex-1 pr-4">
                  {t("HINT_PIN_NEWS")}
                </Text>
                <Controller
                  control={control}
                  name={"isPinned" as any}
                  render={({ field }) => (
                    <ScToggle value={field.value} onChange={field.onChange} />
                  )}
                />
              </View>
            </View>
          )}

          <View className="flex-row justify-between mt-4">
            <View className="flex-row gap-4">
              {secondaryAction && (
                <ScButton
                  text={secondaryAction.label}
                  mode={secondaryAction.mode ?? "outlined"}
                  onPress={secondaryAction.onPress}
                  disabled={secondaryAction.disabled ?? isLoading}
                />
              )}
              {secondaryActions.map((action) => (
                <ScButton
                  key={action.label}
                  text={action.label}
                  mode={action.mode ?? "outlined"}
                  onPress={() => {
                    if (isLoading || action.loading) return;
                    action.onPress();
                  }}
                  disabled={action.disabled ?? isLoading}
                  loading={action.loading ?? isLoading}
                />
              ))}
            </View>
            <View className="flex-row gap-4 ml-auto">
              {primaryActions.map((action) => (
                <ScButton
                  key={action.label}
                  text={action.label}
                  mode={action.mode ?? "contained"}
                  onPress={() => {
                    if (isLoading || action.loading) return;
                    action.onPress();
                  }}
                  disabled={action.disabled ?? isLoading}
                  loading={action.loading ?? isLoading}
                />
              ))}
            </View>
          </View>
        </View>
      </ScrollView>
    </>
  );
}
